import {CalendarIcon} from '@gwc/main'
import {flow, get} from '@yandex-delivery/utils'
import {RangeValue} from '@react-types/shared'
import {memo, useCallback, useState} from 'react'

import {Grid} from '../grid'
import {Input} from '../input'
import {classes} from './classes'
import {DatePickerMobileProps, RangeDatePickerMobileProps} from './types'

const formatISODate = (isoString: string | undefined): string => {
  if (!isoString) return ''

  const date = new Date(isoString)
  if (isNaN(date.valueOf())) {
    console.error(`Invalid date inside RangeDatePickerMobile: ${isoString}`)
  }
  // TODO рассмотреть возможность форматировать "под локаль", не завязываясь на document.documentElement
  return date.toLocaleDateString(document.documentElement.getAttribute('lang') || 'ru')
}

const dateISOToInputValueFormat = (date: string | undefined): string =>
  date !== undefined ? dateToInputValueFormat(new Date(date)) : ''

const dateToInputValueFormat = (date: Date): string =>
  `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
    .getDate()
    .toString()
    .padStart(2, '0')}`

const showPickerOnClick = (e: React.MouseEvent<HTMLInputElement>) => {
  if ('showPicker' in e.currentTarget) {
    e.currentTarget.showPicker()
  }
}

export const RangeDatePickerMobile = memo(
  ({
    value,
    onChange,
    label,
    minValue: minValueDate,
    maxValue: maxValueDate,
    defaultValue,
    ...visualProps
  }: RangeDatePickerMobileProps) => {
    const [inputStartValue, setInputStartValue] = useState<string | undefined>(defaultValue?.start)
    const [inputEndValue, setInputEndValue] = useState<string | undefined>(defaultValue?.end)

    const minValue = minValueDate && dateISOToInputValueFormat(minValueDate)
    const maxValue = maxValueDate && dateISOToInputValueFormat(maxValueDate)

    const handleDateSelect = useCallback(
      (newValue: Partial<RangeValue<string | null>>) => {
        onChange?.({
          start: !!inputStartValue ? new Date(inputStartValue).toISOString() : null,
          end: !!inputEndValue ? new Date(inputEndValue).toISOString() : null,
          ...newValue,
        })
      },
      [inputStartValue, inputEndValue, onChange],
    )

    const handleDateStartSelect = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputStartValue(e.currentTarget.value)
        handleDateSelect({start: e.currentTarget.value})
      },
      [handleDateSelect],
    )

    const handleDateEndSelect = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputEndValue(e.currentTarget.value)
        handleDateSelect({end: e.currentTarget.value})
      },
      [handleDateSelect],
    )

    return (
      <Grid size="s" wrap="nowrap" container>
        <Grid className={classes.rangeSegmentContainer} xs={6} container item>
          <Input
            label={get('start', label)}
            lead={<CalendarIcon />}
            value={formatISODate(value?.start || inputStartValue)}
            readOnly
            {...visualProps}
          />
          <input
            className={classes.fill}
            max={dateISOToInputValueFormat(inputEndValue) || maxValue}
            min={minValue}
            onChange={handleDateStartSelect}
            onPointerUp={showPickerOnClick}
            type="date"
            value={dateISOToInputValueFormat(inputStartValue)}
            required
          />
        </Grid>
        <Grid className={classes.rangeSegmentContainer} xs={6} container item>
          <Input
            label={get('end', label)}
            lead={<CalendarIcon />}
            value={formatISODate(value?.end || inputEndValue)}
            readOnly
            {...visualProps}
          />
          <input
            className={classes.fill}
            max={maxValue}
            min={dateISOToInputValueFormat(inputStartValue) || minValue}
            onChange={handleDateEndSelect}
            onPointerUp={showPickerOnClick}
            type="date"
            value={dateISOToInputValueFormat(inputEndValue)}
            required
          />
        </Grid>
      </Grid>
    )
  },
)

export const DatePickerMobile = memo(
  ({value, maxValue, minValue, onChange, name, ...visualProps}: DatePickerMobileProps) => {
    return (
      <Grid className={classes.relative} container item>
        <Input lead={<CalendarIcon />} value={formatISODate(value || undefined)} readOnly {...visualProps} />
        <input
          className={classes.fill}
          disabled={visualProps.disabled}
          max={maxValue && dateToInputValueFormat(new Date(maxValue))}
          min={minValue && dateToInputValueFormat(new Date(minValue))}
          name={name}
          onChange={flow(get('currentTarget.value'), onChange)}
          onPointerUp={showPickerOnClick}
          type="date"
          required
        />
      </Grid>
    )
  },
)
